import React, { useEffect, useState } from "react";
import { generateObjectId } from "../../../utils/objectIdUtils";
import Label from "../../../components/FormComponents/Label";
import Input from "../../../components/FormComponents/Input";
import Button from "../../../components/FormComponents/Button";
import MaterialIcon from "../../../components/BuilderComponents/MaterialIcon";
import ErrorMessage from "../../../components/FormComponents/ErrorMessage";
import { ReactSortable } from "react-sortablejs";

const validateVariableRow = (row, allVariables) => {
  const trimmedName = row.name.trim();
  const trimmedPath = row.path.trim();

  if (!trimmedName || !trimmedPath) {
    return "Name and path cannot be empty.";
  }

  const isDuplicate = allVariables.some((v) => v.id !== row.id && v.name.trim() === trimmedName);

  if (isDuplicate) {
    return "This name already exists.";
  }

  return "";
};

const HttpOutputVariableBinding = ({ variables, onChange }) => {
  const [newVariable, setNewVariable] = useState({ name: "", path: "" });
  const [error, setError] = useState("");

  const handleAdd = () => {
    const newVar = {
      id: generateObjectId(),
      name: newVariable.name,
      path: newVariable.path
    };

    const validationError = validateVariableRow(newVar, variables);
    if (validationError) {
      setError(validationError);
      return;
    }

    onChange([...variables, { ...newVar, name: newVar.name.trim(), path: newVar.path.trim() }]);
    setNewVariable({ name: "", path: "" });
    setError("");
  };

  const handleNewNameChange = (value) => {
    setNewVariable({ ...newVariable, name: value });
    if (error) setError("");
  };

  const handleNewPathChange = (value) => {
    setNewVariable({ ...newVariable, path: value });
    if (error) setError("");
  };

  const handleRowChange = (id, updatedRow) => {
    const updated = variables.map((v) => (v.id === id ? updatedRow : v));
    onChange(updated);
  };

  const handleRemove = (id) => {
    onChange(variables.filter((v) => v.id !== id));
  };

  useEffect(() => {
    const hasHttpOutput = variables.some((v) => v.name === "http_output" && v.path === "http_output");
    const hasHttpInput = variables.some((v) => v.name === "http_input" && v.path === "http_input");

    const baseVars = [...variables];
    if (!hasHttpOutput) {
      baseVars.unshift({
        id: generateObjectId(),
        name: "http_output",
        path: "http_output"
      });
    }
    if (!hasHttpInput) {
      baseVars.unshift({
        id: generateObjectId(),
        name: "http_input",
        path: "http_input"
      });
    }

    if (baseVars.length !== variables.length) {
      onChange(baseVars);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="d_flex flex_column gap_3">
      <Label>Output Variables</Label>

      <div className="d_flex flex_column gap_2 p_3 border_full" style={{ background: "#f9f9f9", borderRadius: 6 }}>
        <div className="d_flex align_center gap_3">
          <Input
            value={newVariable.name}
            onChange={(e) => handleNewNameChange(e.target.value)}
            placeholder="New Name"
            className="py_2 px_3 border_full"
          />
          <Input
            value={newVariable.path}
            onChange={(e) => handleNewPathChange(e.target.value)}
            placeholder="New Path"
            className="py_2 px_3 border_full"
          />
          <Button onClick={handleAdd}>Add</Button>
        </div>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>

      <OutputVariableTable
        variables={variables}
        onUpdate={handleRowChange}
        onRemove={handleRemove}
        onReorder={onChange}
      />
    </div>
  );
};

const OutputVariableTable = ({ variables, onUpdate, onRemove, onReorder }) => {
  return (
    <div className="border_full border_radius_medium overflow_hidden">
      {/* Header */}
      <div className="d_flex font_weight_semibold py_2 px_3 bg_gray border_bottom">
        <div style={{ width: 32 }} />
        <div style={{ flex: 4 }}>Name</div>
        <div style={{ flex: 4 }}>Path</div>
        <div style={{ flex: 1 }}></div>
      </div>

      {/* Draggable Rows */}
      <ReactSortable
        list={variables}
        setList={onReorder}
        animation={150}
        ghostClass="sortable-ghost"
        handle=".drag_handle"
      >
        {variables.map((v) => (
          <OutputVariableRow key={v.id} item={v} allVariables={variables} onChange={onUpdate} onRemove={onRemove} />
        ))}
      </ReactSortable>
    </div>
  );
};

const OutputVariableRow = ({ item, allVariables, onChange, onRemove }) => {
  const [row, setRow] = useState({ ...item });
  const [error, setError] = useState("");

  const isSpecial =
    (row.name === "http_output" && row.path === "http_output") ||
    (row.name === "http_input" && row.path === "http_input");

  const handleFieldChange = (field, value) => {
    const updatedRow = { ...row, [field]: value };
    setRow(updatedRow);

    const validationError = validateVariableRow(updatedRow, allVariables);
    setError(validationError);

    if (!validationError) {
      onChange(updatedRow.id, {
        ...updatedRow,
        name: updatedRow.name.trim(),
        path: updatedRow.path.trim()
      });
    }
  };

  useEffect(() => {
    setRow(item);
  }, [item]);

  return (
    <div style={{ backgroundColor: isSpecial ? "#fafafa" : "#fff" }}>
      <div className="d_flex align_center py_2 px_3 gap_2">
        {/* Drag Handle */}
        <div className="drag_handle cursor_move" title="Drag to reorder">
          <MaterialIcon name="drag_indicator" />
        </div>

        <div style={{ flex: 4 }}>
          <Input
            value={row.name}
            onChange={(e) => handleFieldChange("name", e.target.value)}
            placeholder="Name"
            disabled={isSpecial}
          />
        </div>
        <div style={{ flex: 4 }}>
          <Input
            value={row.path}
            onChange={(e) => handleFieldChange("path", e.target.value)}
            placeholder="Path"
            disabled={isSpecial}
          />
        </div>
        <div style={{ flex: 1 }} className="d_flex align_center">
          {!isSpecial && (
            <Button onClick={() => onRemove(row.id)} variant="delete">
              <MaterialIcon name="delete" />
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="d_flex align_center px_3 gap_2">
          <ErrorMessage>{error}</ErrorMessage>
        </div>
      )}
    </div>
  );
};

export default HttpOutputVariableBinding;
